import { useMemo } from 'react';
import { CreditAnalysisStatus } from '@/api/commercial/credit-analysis';
import type { CreditAnalysisDocumentReview } from '@/api/commercial/credit-analysis-document-reviews';

export type AccountType = 'PF' | 'MEI' | 'MEI_UNDER_ONE_YEAR' | 'ME';

export interface DocumentField {
  key: string;
  label: string;
  required: boolean;
  description?: string;
  accept?: string;
}

export interface DocumentStatusData {
  status: 'APPROVED' | 'DECLINED';
  reason?: string;
  documentType: string;
}

export interface StateMachineResult {
  // Flags de renderização
  showDocumentRequestForm: boolean;
  showResendButton: boolean;
  showTimeline: boolean;
  showVerdictDetails: boolean;
  showUploadForm: boolean;

  // Dados de validação
  requiredDocuments: DocumentField[];
  declinedDocuments: string[];

  // Document reviews
  documentReviews: CreditAnalysisDocumentReview[];
  pendingDocumentReviews: CreditAnalysisDocumentReview[];
  approvedDocumentReviews: CreditAnalysisDocumentReview[];
  rejectedDocumentReviews: CreditAnalysisDocumentReview[];
  hasPendingReviews: boolean;
  hasRejectedReviews: boolean;
  allDocumentsReviewed: boolean;

  // Mensagens
  statusTitle: string;
  statusMessage: string;
  statusColor: 'red' | 'yellow' | 'green' | 'blue' | 'gray';

  // Flags de ação
  canSendRequest: boolean;
  canUploadDocuments: boolean;
  canResendRequest: boolean;
  canRequestNewAnalysis: boolean;
  canReviewDocuments: boolean;

  // Step completion
  isStepComplete: boolean;
}

interface UseCreditAnalysisStateMachineOptions {
  creditAnalysis: {
    status: CreditAnalysisStatus;
    newAnalysisIn?: string | 'NEXT_PURCHASE';
    documentsStatus?: Record<string, DocumentStatusData>;
  } | null;
  accountType: AccountType;
  currentDate?: Date;
  documentReviews?: CreditAnalysisDocumentReview[];
}

// Documentos requeridos por tipo de conta (baseado no Next.js)
export const fieldsByAccountType: Record<AccountType, DocumentField[]> = {
  PF: [
    {
      key: 'pf_documento_identificacao',
      label: 'Documento de identificação (RG/CIN/CNH/Passaporte)',
      required: true,
      description: 'RG frente e verso ou CNH',
      accept: '.pdf,.jpg,.jpeg,.png',
    },
    {
      key: 'pf_comprovante_endereco',
      label: 'Comprovante de endereço',
      required: true,
      description: 'Comprovante de residência atual (últimos 3 meses)',
      accept: '.pdf',
    },
    {
      key: 'pf_comprovante_renda',
      label: 'Comprovante de renda',
      required: true,
      description: 'Holerite ou declaração de imposto de renda',
      accept: '.pdf',
    },
  ],
  MEI: [
    {
      key: 'mei_mais1_cartao_cnpj',
      label: 'Cartão CNPJ',
      required: true,
      description: 'Cartão CNPJ frente e verso',
      accept: '.pdf,.jpg,.jpeg,.png',
    },
    {
      key: 'mei_mais1_comprovante_endereco',
      label: 'Comprovante de endereço da empresa',
      required: true,
      description: 'Comprovante de residência da empresa (últimos 3 meses)',
      accept: '.pdf',
    },
    {
      key: 'mei_mais1_doc_identificacao_titular',
      label: 'Documento de identificação do titular (RG/CIN/CNH/Passaporte)',
      required: true,
      description: 'RG frente e verso ou CNH',
      accept: '.pdf,.jpg,.jpeg,.png',
    },
    {
      key: 'mei_mais1_dasn_simei',
      label: 'Declaração anual do Simples Nacional (DASN-SIMEI)',
      required: true,
      description: 'DASN-SIMEI',
      accept: '.pdf',
    },
  ],
  MEI_UNDER_ONE_YEAR: [
    {
      key: 'mei_menos1_cartao_cnpj',
      label: 'Cartão CNPJ',
      required: true,
      description: 'Cartão CNPJ frente e verso',
      accept: '.pdf,.jpg,.jpeg,.png',
    },
    {
      key: 'mei_menos1_comprovante_endereco',
      label: 'Comprovante de endereço da empresa',
      required: true,
      description: 'Comprovante de residência da empresa (últimos 3 meses)',
      accept: '.pdf',
    },
    {
      key: 'mei_menos1_doc_identificacao_titular',
      label: 'Documento de identificação do titular (RG/CIN/CNH/Passaporte)',
      required: true,
      description: 'RG frente e verso ou CNH',
      accept: '.pdf,.jpg,.jpeg,.png',
    },
  ],
  ME: [
    {
      key: 'pj_contrato_social',
      label: 'Contrato Social',
      required: true,
      description: 'Contrato social atualizado',
      accept: '.pdf',
    },
    {
      key: 'pj_alteracoes_contrato_social',
      label: 'Alterações do Contrato Social',
      required: false,
      description: 'Alterações do contrato social (se houver)',
      accept: '.pdf',
    },
    {
      key: 'pj_cartao_cnpj',
      label: 'Cartão CNPJ',
      required: true,
      description: 'Cartão CNPJ frente e verso',
      accept: '.pdf,.jpg,.jpeg,.png',
    },
    {
      key: 'pj_procuracao',
      label: 'Procuração (apenas se um terceiro for quem vai assinar o contrato)',
      required: false,
      description: 'Procuração se aplicável',
      accept: '.pdf',
    },
    {
      key: 'pj_comprovante_endereco',
      label: 'Comprovante de endereço da empresa',
      required: true,
      description: 'Comprovante de residência da empresa (últimos 3 meses)',
      accept: '.pdf',
    },
    {
      key: 'pj_alvara_funcionamento',
      label: 'Alvará de funcionamento',
      required: false,
      description: 'Alvará de funcionamento (se aplicável)',
      accept: '.pdf',
    },
    // Sócio/Direitor é tratado separadamente
    {
      key: 'pj_balanco_patrimonial',
      label: 'Balanço patrimonial',
      required: false,
      description: 'Balanço patrimonial (opcional)',
      accept: '.pdf',
    },
    {
      key: 'pj_dre',
      label: 'Demonstração de resultados',
      required: false,
      description: 'DRE (opcional)',
      accept: '.pdf',
    },
    {
      key: 'pj_relatorio_faturamento_6m',
      label: 'Relatório de faturamento (últimos 6 meses)',
      required: false,
      description: 'Relatório de faturamento (opcional)',
      accept: '.pdf',
    },
  ],
};

// Documentos complementares para ME
export const complementaryDocKeys = [
  'pj_balanco_patrimonial',
  'pj_dre',
  'pj_relatorio_faturamento_6m',
] as const;

// Campo de sócio/diretor (tratado separadamente)
export const partnerDirectorDocKey = 'pj_socio_diretor_doc_identificacao';

// Labels dos tipos de conta
export const accountTypeLabels: Record<AccountType, string> = {
  PF: 'Pessoa Física',
  MEI: 'MEI com mais de 1 ano',
  MEI_UNDER_ONE_YEAR: 'MEI com menos de 1 ano',
  ME: 'Empresa que não é MEI',
};

/**
 * Normalizes the account type from CNPJA API to credit analysis document types.
 *
 * CNPJA API returns: 'MEI', 'ME', 'EPP', 'DEMAIS', 'PF'
 * Credit analysis expects: 'PF', 'MEI', 'MEI_UNDER_ONE_YEAR', 'ME'
 *
 * @param accountType - The account type from the API (accounts.accountType field)
 * @param simeiSince - The SIMEI registration date (accounts.simeiSince field)
 * @param documentType - The document type ('cpf' | 'cnpj') - used as fallback when accountType is undefined
 * @returns The normalized account type for credit analysis
 */
export function normalizeAccountTypeForCreditAnalysis(
  accountType: string | undefined | null,
  simeiSince: string | undefined | null,
  documentType?: 'cpf' | 'cnpj',
): AccountType {
  // Pessoa Física - explicit accountType OR derived from document type
  if (accountType === 'PF' || (!accountType && documentType === 'cpf')) {
    return 'PF';
  }

  // MEI - verifica se tem menos de 1 ano
  if (accountType === 'MEI' || simeiSince) {
    if (simeiSince) {
      const startDate = new Date(simeiSince);
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      return startDate < oneYearAgo ? 'MEI' : 'MEI_UNDER_ONE_YEAR';
    }
    return 'MEI';
  }

  // EPP, ME, DEMAIS → mesma documentação de ME
  return 'ME';
}

const statusConfig: Record<
  CreditAnalysisStatus,
  Pick<StateMachineResult, 'statusTitle' | 'statusMessage' | 'statusColor' | 'isStepComplete'>
> = {
  NOT_STARTED: {
    statusTitle: 'Análise não iniciada',
    statusMessage: 'Necessário solicitar documentação ao cliente.',
    statusColor: 'red',
    isStepComplete: false,
  },
  PENDING_EXTERNAL_DOCS: {
    statusTitle: 'Aguardando documentos',
    statusMessage: 'Documentos solicitados, aguardando retorno do cliente!',
    statusColor: 'yellow',
    isStepComplete: false,
  },
  PENDING_ANALYSIS: {
    statusTitle: 'Aguardando Análise',
    statusMessage: 'Documentos enviados, aguardando início da análise!',
    statusColor: 'blue',
    isStepComplete: false,
  },
  IN_ANALYSIS: {
    statusTitle: 'Em análise',
    statusMessage: 'Análise de crédito em andamento.',
    statusColor: 'blue',
    isStepComplete: false,
  },
  DOC_DECLINED: {
    statusTitle: 'Documentação reprovada',
    statusMessage: 'Documentação reprovada. Por favor, envie novos documentos.',
    statusColor: 'red',
    isStepComplete: false,
  },
  APPROVED: {
    statusTitle: 'Aprovada',
    statusMessage: 'Análise de crédito aprovada com sucesso!',
    statusColor: 'green',
    isStepComplete: true,
  },
  PARTIALLY_APPROVED: {
    statusTitle: 'Parcialmente Aprovada',
    statusMessage: 'Análise de crédito aprovada com restrições.',
    statusColor: 'yellow',
    isStepComplete: true,
  },
  DECLINED: {
    statusTitle: 'Reprovada',
    statusMessage: 'Análise de crédito reprovada.',
    statusColor: 'red',
    isStepComplete: false,
  },
  EXPIRED: {
    statusTitle: 'Análise expirada',
    statusMessage: 'Análise expirada. Necessário nova análise.',
    statusColor: 'red',
    isStepComplete: false,
  },
};

export function useCreditAnalysisStateMachine({
  creditAnalysis,
  accountType,
  currentDate = new Date(),
  documentReviews = [],
}: UseCreditAnalysisStateMachineOptions): StateMachineResult {
  const status: CreditAnalysisStatus = creditAnalysis?.status ?? CreditAnalysisStatus.NOT_STARTED;

  const isExpired = useMemo(() => {
    if (!creditAnalysis || (status !== 'APPROVED' && status !== 'PARTIALLY_APPROVED')) {
      return false;
    }

    if (creditAnalysis.newAnalysisIn === 'NEXT_PURCHASE') {
      return false; // Só expira na próxima compra
    }

    if (creditAnalysis.newAnalysisIn) {
      const expiryDate = new Date(creditAnalysis.newAnalysisIn);
      return currentDate > expiryDate;
    }

    return false;
  }, [creditAnalysis, status, currentDate]);

  const effectiveStatus = isExpired ? 'EXPIRED' : status;
  const config = statusConfig[effectiveStatus];

  // Documentos reprovados
  const declinedDocuments = useMemo(() => {
    if (effectiveStatus !== 'DOC_DECLINED' || !creditAnalysis?.documentsStatus) {
      return [];
    }

    return Object.entries(creditAnalysis.documentsStatus)
      .filter(([, data]) => data.status === 'DECLINED')
      .map(([key]) => key);
  }, [creditAnalysis, effectiveStatus]);

  // Documentos obrigatórios baseados no estado e tipo de conta
  const requiredDocuments = useMemo<DocumentField[]>(() => {
    if (effectiveStatus === 'DOC_DECLINED' && declinedDocuments.length > 0) {
      // Retornar apenas documentos reprovados
      const allFields = fieldsByAccountType[accountType];
      return allFields.filter((field) => declinedDocuments.includes(field.key));
    }
    // Retornar apenas documentos obrigatórios (excluindo complementares e sócio)
    return fieldsByAccountType[accountType].filter((f) => {
      // Para ME, excluímos documentos complementares e o campo de sócio/direitor
      if (accountType === 'ME') {
        return !complementaryDocKeys.includes(f.key as any) && f.key !== partnerDirectorDocKey;
      }
      return f.required;
    });
  }, [accountType, effectiveStatus, declinedDocuments]);

  // Document reviews categorization
  const pendingDocumentReviews = useMemo(() => {
    return documentReviews.filter((review) => review.status === 'pending');
  }, [documentReviews]);

  const approvedDocumentReviews = useMemo(() => {
    return documentReviews.filter((review) => review.status === 'approved');
  }, [documentReviews]);

  const rejectedDocumentReviews = useMemo(() => {
    return documentReviews.filter((review) => review.status === 'rejected');
  }, [documentReviews]);

  const hasPendingReviews = useMemo(() => {
    return pendingDocumentReviews.length > 0;
  }, [pendingDocumentReviews.length]);

  const hasRejectedReviews = useMemo(() => {
    return rejectedDocumentReviews.length > 0;
  }, [rejectedDocumentReviews.length]);

  const allDocumentsReviewed = useMemo(() => {
    if (documentReviews.length === 0) return false;
    return documentReviews.every((review) => review.status !== 'pending');
  }, [documentReviews]);

  const canReviewDocuments = useMemo(() => {
    return (
      effectiveStatus === 'PENDING_EXTERNAL_DOCS' ||
      effectiveStatus === 'PENDING_ANALYSIS' ||
      effectiveStatus === 'IN_ANALYSIS'
    );
  }, [effectiveStatus]);

  return {
    // Flags de renderização
    showDocumentRequestForm: effectiveStatus === 'NOT_STARTED',
    showResendButton: effectiveStatus === 'PENDING_EXTERNAL_DOCS',
    showTimeline: creditAnalysis !== null && effectiveStatus !== 'NOT_STARTED',
    showVerdictDetails:
      effectiveStatus === 'APPROVED' ||
      effectiveStatus === 'PARTIALLY_APPROVED' ||
      effectiveStatus === 'DECLINED',
    showUploadForm:
      effectiveStatus === 'DOC_DECLINED' ||
      effectiveStatus === 'NOT_STARTED' ||
      effectiveStatus === 'EXPIRED',

    // Dados de validação
    requiredDocuments,
    declinedDocuments,

    // Document reviews
    documentReviews,
    pendingDocumentReviews,
    approvedDocumentReviews,
    rejectedDocumentReviews,
    hasPendingReviews,
    hasRejectedReviews,
    allDocumentsReviewed,

    // Mensagens
    statusTitle: config.statusTitle,
    statusMessage: config.statusMessage,
    statusColor: config.statusColor,

    // Flags de ação
    canSendRequest: effectiveStatus === 'NOT_STARTED' || effectiveStatus === 'EXPIRED',
    canUploadDocuments:
      effectiveStatus === 'NOT_STARTED' ||
      effectiveStatus === 'DOC_DECLINED' ||
      effectiveStatus === 'EXPIRED',
    canResendRequest: effectiveStatus === 'PENDING_EXTERNAL_DOCS',
    canRequestNewAnalysis: isExpired || effectiveStatus === 'DECLINED',
    canReviewDocuments,

    // Step completion
    isStepComplete: config.isStepComplete,
  };
}
